import { type Key, useMemo, useState } from 'react';
import { BiComment, BiDislike, BiDotsHorizontal, BiLike, BiShow, BiTrash } from 'react-icons/bi';
import { MdOutlineHideSource } from 'react-icons/md';
import { iconClasses } from '@/config/job';
import { Description } from '@/core/molecules/Comment/Description';
import { ActionDropdown, Confirm, cn } from '@/core/ui';
import type { QuestionComment } from '@/graphql/gql/graphql';

type CommentActionItem = {
  key: string;
  className?: string;
  startContent: React.ReactNode;
  title: string;
  color?: 'default' | 'danger' | 'warning' | 'secondary';
  operation: string;
  type: 'success' | 'danger' | 'warn' | 'info' | 'default';
  description: string;
  isVisible: (comment: QuestionComment) => boolean;
};

const COMMENT_ACTIONS: Record<string, CommentActionItem> = {
  DELETE: {
    key: 'delete',
    className: 'text-xs py-1',
    startContent: <BiTrash className={cn(iconClasses, 'text-danger-300')} />,
    title: 'Delete',
    color: 'danger',
    operation: 'delete',
    type: 'danger',
    description: 'Are you sure you want to delete this comment?',
    isVisible: () => true,
  },
  HIDE: {
    key: 'hide',
    className: 'text-xs py-1',
    startContent: <MdOutlineHideSource className={cn(iconClasses, 'text-warning-500')} />,
    title: 'Hide',
    color: 'warning',
    operation: 'hide',
    type: 'warn',
    description: 'Are you sure you want to hide this comment?',
    isVisible: (comment) => !comment.review,
  },
  UNHIDE: {
    key: 'unhide',
    className: 'text-xs py-1',
    startContent: <BiShow className={cn(iconClasses, 'text-secondary-500')} />,
    title: 'Unhide',
    color: 'secondary',
    operation: 'unhide',
    type: 'warn',
    description: 'Are you sure you want to unhide this comment?',
    isVisible: (comment) => comment.review === true,
  },
  RESET_LIKES: {
    key: 'resetLikes',
    className: 'text-xs py-1',
    startContent: <BiLike className={cn(iconClasses)} />,
    title: 'Reset Likes',
    operation: 'resetLikes',
    type: 'default',
    description: 'Are you sure you want to reset the likes for this comment?',
    isVisible: (comment) => (comment.likes ?? 0) > 0,
  },
  RESET_DISLIKES: {
    key: 'resetDislikes',
    className: 'text-xs py-1',
    startContent: <BiDislike className={cn(iconClasses)} />,
    title: 'Reset Dislikes',
    operation: 'resetDislikes',
    type: 'default',
    description: 'Are you sure you want to reset the dislikes for this comment?',
    isVisible: (comment) => (comment.dislikes ?? 0) > 0,
  },
  REVIEW: {
    key: 'review',
    className: 'text-xs py-1',
    startContent: <BiComment className={cn(iconClasses)} />,
    title: 'Review',
    operation: 'review',
    type: 'default',
    description: 'Are you sure you want to add this comment to review?',
    isVisible: (comment) => !comment.recordReviewId,
  },
  UNREVIEW: {
    key: 'unreview',
    className: 'text-xs py-1',
    startContent: <BiComment className={cn(iconClasses)} />,
    title: 'Unreview',
    operation: 'unreview',
    type: 'default',
    description: 'Are you sure you want to remove this comment from review?',
    isVisible: (comment) => !!comment.recordReviewId,
  },
};

export const CommentActions = ({
  comment,
  onAction,
  className,
}: {
  comment: QuestionComment;
  onAction: (ids: (string | number)[], operation: string) => void;
  className?: string;
}) => {
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const actionItems = useMemo(() => {
    return Object.values(COMMENT_ACTIONS)
      .filter((action) => action.isVisible(comment))
      .map((action) => ({
        key: action.key,
        className: action.className,
        startContent: action.startContent,
        title: action.title,
        color: action.color,
      }));
  }, [comment]);

  const handleAction = (key: Key) => {
    const action = Object.values(COMMENT_ACTIONS).find((a) => a.key === key);
    if (action) {
      setSelectedAction(action.operation);
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    if (selectedAction) {
      onAction([String(comment.id)], selectedAction);
      setIsConfirmOpen(false);
      setSelectedAction(null);
    }
  };

  const currentAction = selectedAction
    ? Object.values(COMMENT_ACTIONS).find((a) => a.operation === selectedAction)
    : null;

  const replyCount = comment.replies?.length ?? 0;

  const getDescriptionType = (
    operation: string,
  ): 'hide' | 'delete' | 'resolve' | 'unhide' | 'resetLikes' | 'resetDislikes' | 'review' | 'unreview' => {
    switch (operation) {
      case 'delete':
        return 'delete';
      case 'hide':
        return 'hide';
      case 'unhide':
        return 'unhide';
      case 'resetLikes':
        return 'resetLikes';
      case 'resetDislikes':
        return 'resetDislikes';
      case 'review':
        return 'review';
      case 'unreview':
        return 'unreview';
      default:
        return 'resolve';
    }
  };

  if (actionItems.length === 0) {
    return null;
  }

  return (
    <>
      <ActionDropdown
        offset={0}
        classNames={{
          content: 'shadow-sm border border-default-200 rounded-md min-w-[130px]',
        }}
        onAction={handleAction}
        items={actionItems}
        className={className}
      >
        <div className='hover:bg-content2 flex justify-center items-center rounded-md px-1 py-0.5 cursor-pointer text-default-400'>
          <BiDotsHorizontal className='size-5 cursor-pointer text-default-400' />
        </div>
      </ActionDropdown>

      {currentAction && (
        <Confirm
          type={currentAction.type}
          title={`${currentAction.title} Comment`}
          isOpen={isConfirmOpen}
          onOpenChange={setIsConfirmOpen}
          message={
            <Description
              comment={comment.comment || ''}
              replyCount={replyCount}
              type={getDescriptionType(currentAction.operation)}
              description={currentAction.description}
            />
          }
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
